import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SearchResult } from "@/types/inventory";
import { streamAllSearches } from "@/services/inventoryService";
import SearchTermCard from "@/components/SearchTermCard";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Search, Loader } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { handleAPIError } from "@/utils/apiErrorHandler";
import { toWordCase } from "@/lib/textUtils";

const InventoryDashboard = () => {
  const [inventoryData, setInventoryData] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [remainingSearches, setRemainingSearches] = useState(3);
  const [itemsAwaitingStoreData, setItemsAwaitingStoreData] = useState<Set<string>>(new Set());
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Load search count from session storage for guest users
    if (!isAuthenticated) {
      const storedCount = sessionStorage.getItem('guestSearchCount');
      const storedRemaining = sessionStorage.getItem('guestRemainingSearches');
      if (storedCount) {
        setSearchCount(parseInt(storedCount, 10));
      }
      if (storedRemaining) {
        setRemainingSearches(parseInt(storedRemaining, 10));
      }
    }
  }, [isAuthenticated]);

  // Helper function to update guest search counters
  const updateGuestSearchCounters = (newSearchCount: number, newRemainingSearches: number) => {
    console.log(`🔍 [GUEST_COUNTER] Updating counters - searches: ${newSearchCount}, remaining: ${newRemainingSearches}`);
    setSearchCount(newSearchCount);
    setRemainingSearches(newRemainingSearches);
    sessionStorage.setItem('guestSearchCount', newSearchCount.toString());
    sessionStorage.setItem('guestRemainingSearches', newRemainingSearches.toString());
  };

  const handleManualCheck = async () => {
    const trimmed = searchInput.trim();
    
    // Normalize to title case
    const normalizedSearchTerm = toWordCase(trimmed);

    if (trimmed.length < 4) {
      toast.warning("Search term must be at least 4 characters.");
      return;
    }

    // Check if guest user has reached search limit - show message instead of blocking
    if (!isAuthenticated && remainingSearches <= 0) {
      toast.error("Please sign up for an account to search for more items.");
      return;
    }

    console.log(`🔍 [GUEST_SEARCH] Starting search for: "${trimmed}" (authenticated: ${isAuthenticated}, remaining searches: ${remainingSearches})`);

    setLoading(true);
    setInventoryData([]);
    setHasSearched(true);
    setItemsAwaitingStoreData(new Set());
    
    try {
      // Stream fresh data from backend (no localStorage caching)
      await streamAllSearches(
        [normalizedSearchTerm],
        // On each update for a specific search
        (index, result) => {
          console.log(`🔍 [GUEST_SEARCH] Update received for index: ${index} with ${result.items.length} items (authenticated: ${isAuthenticated})`);
          
          // Update search counts from backend response if provided
          if (result.searchCount !== undefined) {
            console.log(`🔍 [GUEST_SEARCH] Updating search count to: ${result.searchCount}`);
            setSearchCount(result.searchCount);
            if (!isAuthenticated) {
              sessionStorage.setItem('guestSearchCount', result.searchCount.toString());
            }
          }
          
          if (result.remainingSearches !== undefined) {
            console.log(`🔍 [GUEST_SEARCH] Updating remaining searches to: ${result.remainingSearches}`);
            setRemainingSearches(result.remainingSearches);
            if (!isAuthenticated) {
              sessionStorage.setItem('guestRemainingSearches', result.remainingSearches.toString());
            }
          }
          
          // Track which items are still awaiting store data
          const newItemsAwaitingStoreData = new Set<string>();
          
          result.items.forEach(item => {
            // Check if item has proper store data (not placeholder)
            const hasStoreData = item.stores && 
                                item.stores.length > 0 && 
                                !(item.stores.length === 1 && item.stores[0].storeNumber === "No data available in table");
            
            // If no proper store data and storeQty is 0, it's still awaiting
            if (!hasStoreData && item.storeQty === 0) {
              newItemsAwaitingStoreData.add(item.id);
            }
          });
          
          setItemsAwaitingStoreData(newItemsAwaitingStoreData);
          
          // Update inventory data immediately - this shows name, price, warehouse qty, on order qty
          setInventoryData([result]);
          
          // Stop loading indicator once we have items to show
          if (result.items.length > 0) {
            setLoading(false);
          }
        },
        // On all searches complete
        (finalResults) => {
          console.log(`✅ [GUEST_SEARCH] All searches complete, final results: ${finalResults.length} results (authenticated: ${isAuthenticated})`);
          setInventoryData(finalResults);
          setLoading(false);
          toast.success("Inventory check completed");
          
          // Clear all awaiting items since search is complete
          setItemsAwaitingStoreData(new Set());
          
          // For guest users, if we completed a successful search and no backend counters were provided,
          // we need to manually update the counters
          if (!isAuthenticated && finalResults.length > 0) {
            const hasBackendCounters = finalResults.some(result => 
              result.searchCount !== undefined || result.remainingSearches !== undefined
            );
            
            if (!hasBackendCounters) {
              console.log(`🔍 [GUEST_COUNTER] No backend counters found, manually updating for successful search`);
              const newCount = searchCount + 1;
              const newRemaining = Math.max(0, remainingSearches - 1);
              updateGuestSearchCounters(newCount, newRemaining);
            }
          }
        }
      );
    } catch (error: any) {
      console.error(`🔥 [GUEST_SEARCH] Error fetching inventory (authenticated: ${isAuthenticated}):`, error);
      
      // Handle authentication required error
      if (error.message === 'AUTH_REQUIRED') {
        navigate('/signup');
        return;
      }
      
      // Handle guest limit exceeded error with robust data extraction
      if (!isAuthenticated && error.isGuestLimitError && error.backendData) {
        console.log(`🔍 [GUEST_LIMIT] Processing guest limit error with backend data:`, error.backendData);
        const { searchCount: backendSearchCount, remainingSearches: backendRemaining, maxSearches } = error.backendData;
        
        // Update counters with backend data
        updateGuestSearchCounters(backendSearchCount, backendRemaining);
        
        toast.error(`You've used all ${maxSearches} guest searches. Sign up for unlimited searches!`);
      } else if (!isAuthenticated) {
        // Fallback handling for guest users when backend data is not available
        console.log(`🔍 [GUEST_FALLBACK] No backend data in error, using fallback logic`);
        
        // If we detect it's a limit exceeded error, set remaining to 0
        if (error.message && (error.message.includes('limit') || error.message.includes('exceeded'))) {
          console.log(`🔍 [GUEST_FALLBACK] Detected limit exceeded, setting remaining to 0`);
          updateGuestSearchCounters(3, 0);
          toast.error("You've reached your search limit. Please sign up to continue searching.");
        } else {
          // For other errors, still decrement if we have searches remaining
          if (remainingSearches > 0) {
            const newRemaining = remainingSearches - 1;
            const newCount = searchCount + 1;
            console.log(`🔍 [GUEST_FALLBACK] Error occurred, decrementing counters`);
            updateGuestSearchCounters(newCount, newRemaining);
          }
          
          // Show different error messages based on the error
          if (error.message.includes('500')) {
            toast.error("Our servers are experiencing issues. Please try again in a few minutes.");
          } else if (error.message.includes('Network')) {
            toast.error("Network connection issue. Please check your internet and try again.");
          } else {
            toast.error("Failed to check inventory. Please try again.");
          }
        }
      } else {
        // Handle authenticated user errors
        toast.error("Failed to check inventory. Please try again.");
      }
      
      setLoading(false);
    }
  };

  const inStockCount = inventoryData.reduce((count, result) => {
    return count + result.items.filter((item) => item.storeQty > 0).length;
  }, 0);

  const totalItemCount = inventoryData.reduce((count, result) => count + result.items.length, 0);

  return (
    <div className="space-y-6">
      {/* Search input section */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label htmlFor="searchTerm" className="block font-medium">
            Enter a search term (min. 4 characters)
          </label>
          {isAuthenticated && (
            <span className="text-sm text-muted-foreground">
              Searches Remaining: Unlimited
            </span>
          )}
        </div>

        <Input
          id="searchTerm"
          className="w-full"
          placeholder="e.g. Eagle Rare, Blanton's, E H Taylor"
          maxLength={256}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        
        <div className="mt-2 flex justify-center">
          <Button
            onClick={handleManualCheck}
            className="bg-whiskey-amber hover:bg-whiskey-amber/80 text-black"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="mr-2" />
                Search Inventory
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Results */}
      <div>
        {hasSearched ? (
          <>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-1">
              <h3 className="text-xl font-semibold">Search Results: {totalItemCount}</h3>
              {!isAuthenticated && (
                <span className="text-xs text-muted-foreground">
                  Guest searches remaining: {remainingSearches}/3
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {inventoryData.length > 0 ? (
                inventoryData.map((result) => (
                  <SearchTermCard 
                    key={result.searchTerm} 
                    result={result} 
                    itemsAwaitingStoreData={itemsAwaitingStoreData}
                  />
                ))
              ) : (
                <div className="col-span-full text-center p-8 bg-muted/10 rounded-lg">
                  <p className="text-muted-foreground">
                    No inventory data found for your search term.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center p-8 bg-muted/10 rounded-lg">
            <Search className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              Search for your favorite whiskey to check current availability
            </p>
          </div>
        )}
      </div>
      
      {/* CTA after results */}
      {hasSearched && !isAuthenticated && (
        <Card className="mt-4 border-whiskey-amber/40 bg-whiskey-amber/5">
          <CardContent className="pt-6">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-medium">Want to know when it's back in stock?</h3>
              <p>Sign up to receive SMS alerts when your favorite whiskeys become available at your local store.</p>
              <Link to="/signup">
                <Button className="bg-whiskey-amber hover:bg-whiskey-amber/80 text-black mt-2">
                  Sign up and Create an Alert
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InventoryDashboard;
